import React, { useState, useEffect, useCallback } from 'react';
import EContractConfig from '../../../components/configuration/econtract/EContractConfig';
import EContractModal from '../../../components/configuration/econtract/EContractModal';
import { withRouter } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import * as clubsAPI from '../../../lib/api/clubs';
import AskRemoveModal from '../../../components/common/AskRemoveModal';
import imageCompression from 'browser-image-compression';
import { uploadImageToServer, hasEmoji } from '../../../lib/commonFunc/commonFunc';
import { tempSetClub } from '../../../modules/clubs/club';

const EContractContainer = ({ history }) => {
  const dispatch = useDispatch();
  const { club, socketKey } = useSelector(({ club, socket }) => ({
    club: club.defaultClub,
    socketKey: socket.socketKey,
  }));

  const [loading, setLoading] = useState(false);
  const [eContractActive, setEContractActive] = useState(0);
  const [coachActive, setCoachActive] = useState(0);
  const [registActive, setRegistActive] = useState(0);
  const [kioskActive, setKioskActive] = useState(0);
  const [currentID, setCurrentID] = useState('');
  const [eContractList, setEContractList] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedEContract, setSelectedEContract] = useState(null);
  const [removeModalVisible, setRemoveModalVisible] = useState(false);
  const [removeTargetID, setRemoveTargetID] = useState(null);

  // 초기 데이터 로드
  useEffect(() => {
    if (club && socketKey) {
        setEContractActive(club.eContractActive || 0);
      loadEContractData();
    }
  }, [club, socketKey]);

  const loadEContractData = async () => {
    if (!club || !socketKey) return;
    
    try {
      setLoading(true);
      
      // 클럽 데이터에서 전자계약서 활성화 상태 가져오기
      const clubRes = await clubsAPI.readClub({
        clubID: club.clubID,
        socketKey,
      });
      
      if (clubRes.data && clubRes.data.club) {
        setEContractActive(clubRes.data.club.eContractActive || 0);
      }
      
      // 전자계약서 정보 가져오기
      const res = await clubsAPI.loadEContract({
        clubID: club.clubID,
        socketKey,
      });
      
      if (res.data && res.data.result === 1) {
        setCoachActive(res.data.coachActive || 0);
        setRegistActive(res.data.registActive || 0);
        setKioskActive(res.data.kioskActive || 0);
        setCurrentID(res.data.currentID || '');
        setEContractList(res.data.eContractData || []);
      }
    } catch (e) {
      console.error(e);
      alert('전자계약서 데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 전자계약서 활성화/비활성화
  const handleToggleActive = async (active) => {
    if (!club || !socketKey) return;
    
    try {
      setLoading(true);
      const res = await clubsAPI.updateEContractActive({
        clubID: club.clubID,
        eContractActive: active,
        socketKey,
      });

      console.log("updateEContractActive : ", res.data);
      
      if (res.data && res.data.result) {
        setEContractActive(active);
        if (club) {
          dispatch(tempSetClub({
            ...club,
            eContractActive: active,
          }));
        }
        if (active === 0) {
          // 비활성화 시 상세 설정도 초기화할 수 있음
          setCoachActive(0);
          setRegistActive(0);
          setKioskActive(0);
          setCurrentID('');
        }
      } else {
        alert('전자계약서 활성화 상태 변경에 실패했습니다.');
      }
    } catch (e) {
      console.error(e);
      alert('전자계약서 활성화 상태 변경 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 강사앱 사용 토글
  const handleToggleCoachActive = async (active) => {
    if (!club || !socketKey) return;
    
    try {
      const res = await clubsAPI.updateEContractInfo({
        clubID: club.clubID,
        coachActive: active,
        registActive,
        kioskActive,
        currentID,
        socketKey,
      });
      
      if (res.data && res.data.result === 1) {
        setCoachActive(active);
      } else {
        alert('강사앱 사용 설정 변경에 실패했습니다.');
      }
    } catch (e) {
      console.error(e);
      alert('강사앱 사용 설정 변경 중 오류가 발생했습니다.');
    }
  };

  // 회원 클럽등록시 사용 토글
  const handleToggleRegistActive = async (active) => {
    if (!club || !socketKey) return;
    
    try {
      const res = await clubsAPI.updateEContractInfo({
        clubID: club.clubID,
        coachActive,
        registActive: active,
        kioskActive,
        currentID,
        socketKey,
      });
      
      if (res.data && res.data.result === 1) {
        setRegistActive(active);
      } else {
        alert('회원 등록 시 사용 설정 변경에 실패했습니다.');
      }
    } catch (e) {
      console.error(e);
      alert('회원 등록 시 사용 설정 변경 중 오류가 발생했습니다.');
    }
  };

  // 키오스크 사용 토글
  const handleToggleKioskActive = async (active) => {
    if (!club || !socketKey) return;
    
    try {
      const res = await clubsAPI.updateEContractInfo({
        clubID: club.clubID,
        coachActive,
        registActive,
        kioskActive: active,
        currentID,
        socketKey,
      });
      
      if (res.data && res.data.result === 1) {
        setKioskActive(active);
      } else {
        alert('강사앱 사용 설정 변경에 실패했습니다.');
      }
    } catch (e) {
      console.error(e);
      alert('강사앱 사용 설정 변경 중 오류가 발생했습니다.');
    }
  };

  // 전자계약서 활성화/비활성화 (리스트에서 클릭)
  const handleActivateContract = async (eContractID) => {
    if (!club || !socketKey) return;
    
    // 이미 활성화된 계약서를 다시 클릭하면 비활성화
    const newCurrentID = currentID === eContractID ? '' : eContractID;
    
    try {
      const res = await clubsAPI.updateEContractInfo({
        clubID: club.clubID,
        coachActive,
        registActive,
        kioskActive,
        currentID: newCurrentID,
        socketKey,
      });
      
      if (res.data && res.data.result === 1) {
        setCurrentID(newCurrentID);
      } else {
        alert('전자계약서 활성화 설정에 실패했습니다.');
      }
    } catch (e) {
      console.error(e);
      alert('전자계약서 활성화 설정 중 오류가 발생했습니다.');
    }
  };

  // 전자계약서 추가/수정 모달 열기
  const handleAddEContract = () => {
    setSelectedEContract(null);
    setModalVisible(true);
  };

  const handleModifyEContract = (eContract) => {
    setSelectedEContract(eContract);
    setModalVisible(true);
  };

  // 도장 이미지 업로드
  const handleUploadStamp = async (file) => {
    if (!club) return null;
    
    try {
      // 이미지 압축
      const options = { 
        maxSizeMB: 0.256
      };
      
      const compressedFile = await imageCompression(file, options);
      
      // 파일명 생성
      const timestamp = Math.floor(Date.now() / 1000);
      const random = Math.random();
      const fileName = `${club.clubID}_econtract_stamp_${timestamp}_${random}.jpg`;
      
      // 서버에 업로드
      await uploadImageToServer(compressedFile, fileName, 'clubdata/econtract', compressedFile.type);
      
      // 업로드된 이미지 URL 반환
      return `econtract/${fileName}`;
      
    } catch (e) {
      console.error(e);
      alert('도장 이미지 업로드 중 오류가 발생했습니다.');
      return null;
    }
  };

  // 전자계약서 추가/수정 확인
  const handleConfirmEContract = async (data) => {
    if (!club || !socketKey) return;

    // utf8mb3 DB 는 이모지(4바이트 문자)를 저장할 수 없어 등록/수정을 차단한다
    if (hasEmoji(data.title) || hasEmoji(data.text)) {
      alert('이모지를 사용할 수 없습니다.');
      return;
    }

    try {
      setLoading(true);
      const res = await clubsAPI.updateEContract({
        clubID: club.clubID,
        eContractID: data.eContractID,
        title: data.title,
        text: data.text,
        stempURL: data.stempURL,
        socketKey,
      });
      
      if (res.data && res.data.result === 1) {
        setModalVisible(false);
        setSelectedEContract(null);
        await loadEContractData();
      } else {
        alert('전자계약서 저장에 실패했습니다.');
      }
    } catch (e) {
      console.error(e);
      alert('전자계약서 저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 전자계약서 삭제 모달 열기
  const handleDeleteEContract = (eContractID) => {
    setRemoveTargetID(eContractID);
    setRemoveModalVisible(true);
  };

  // 전자계약서 삭제 확인
  const handleConfirmDelete = async () => {
    if (!club || !socketKey || !removeTargetID) return;
    
    try {
      setLoading(true);
      const res = await clubsAPI.deleteEContract({
        clubID: club.clubID,
        eContractID: removeTargetID,
        socketKey,
      });
      
      if (res.data && res.data.result === 1) {
        setRemoveModalVisible(false);
        setRemoveTargetID(null);
        
        // 삭제된 계약서가 현재 활성화된 계약서였다면 초기화
        if (removeTargetID === currentID) {
          await handleActivateContract(removeTargetID); // 비활성화
        }
        
        await loadEContractData();
      } else {
        alert(res.data?.message || '전자계약서 삭제에 실패했습니다.');
      }
    } catch (e) {
      console.error(e);
      alert('전자계약서 삭제 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (!club || !socketKey) {
    return null;
  }

  return (
    <>
      <EContractConfig
        eContractActive={eContractActive}
        coachActive={coachActive}
        registActive={registActive}
        kioskActive={kioskActive}
        currentID={currentID}
        eContractList={eContractList}
        loading={loading}
        onToggleActive={handleToggleActive}
        onToggleCoachActive={handleToggleCoachActive}
        onToggleRegistActive={handleToggleRegistActive}
        onToggleKioskActive={handleToggleKioskActive}
        onAddEContract={handleAddEContract}
        onModifyEContract={handleModifyEContract}
        onDeleteEContract={handleDeleteEContract}
        onActivateContract={handleActivateContract}
      />
      
      <EContractModal
        visible={modalVisible}
        onClose={() => {
          setModalVisible(false);
          setSelectedEContract(null);
        }}
        onConfirm={handleConfirmEContract}
        eContract={selectedEContract}
        onUploadStamp={handleUploadStamp}
        club={club}
      />
      
      <AskRemoveModal
        visible={removeModalVisible}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setRemoveModalVisible(false);
          setRemoveTargetID(null);
        }}
        text="전자계약서"
      />
    </>
  );
};

export default withRouter(EContractContainer);
